import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { InvestmentsAPI, InvestmentPayload } from '../services/investments.ts';
import { formatCurrency, formatCurrencyInput, applyCurrencyMask, parseCurrency, getCurrencyLocale } from '../utils/currency.ts';
import { ArrowLeftIcon, CheckIcon, DocumentTextIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const EditInvestment: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<InvestmentPayload>({
    description: '',
    category: 'stocks',
    investedValue: 0,
    currentValue: undefined,
    status: 'active',
    currency: 'BRL',
  });
  const [investedValueDisplay, setInvestedValueDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const data = await InvestmentsAPI.get(id);
        setForm({
          description: data.description,
          category: data.category,
          investedValue: data.investedValue,
          currentValue: data.currentValue,
          status: data.status,
          currency: (data as any).currency || 'BRL',
        });
        // Inicializar displays com formatação direta (evita divisão por 100)
        setInvestedValueDisplay(formatCurrencyInput(Number(data.investedValue || 0)));
        setCurrentValueDisplay(
          data.currentValue !== undefined && data.currentValue !== null
            ? formatCurrencyInput(Number(data.currentValue))
            : ''
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const updateMutation = useMutation(async () => InvestmentsAPI.update(String(id), form), {
    onSuccess: () => {
      // Garantir atualização imediata nas telas relevantes
      queryClient.invalidateQueries('investments');
      queryClient.invalidateQueries(['investment', id]);
      navigate(`/investments/${id}`);
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    } as any));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCurrencyChange = (field: 'investedValue' | 'currentValue', rawValue: string) => {
    const locale = getCurrencyLocale(form.currency || 'BRL');
    const masked = applyCurrencyMask(rawValue, locale);
    const numeric = parseCurrency(masked, locale);
    if (field === 'investedValue') {
      setInvestedValueDisplay(masked);
    } else {
      setCurrentValueDisplay(masked);
    }
    setForm(prev => ({
      ...prev,
      [field]: numeric,
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!form.description || !form.description.trim()) {
      nextErrors.description = 'Descrição é obrigatória';
    }
    if (!form.category) {
      nextErrors.category = 'Categoria é obrigatória';
    }
    if (!form.status) {
      nextErrors.status = 'Status é obrigatório';
    }
    if (!form.investedValue || form.investedValue <= 0) {
      nextErrors.investedValue = 'Valor investido deve ser maior que zero';
    }
    if (form.currentValue !== undefined && form.currentValue < 0) {
      nextErrors.currentValue = 'Valor atual não pode ser negativo';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/investments/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Editar Investimento</h1>
          <p className="text-gray-600 mt-2">Atualize os dados do investimento</p>
        </div>

        {/* Seção: Informações Básicas */}
        <form onSubmit={(e) => { e.preventDefault(); if (validate()) updateMutation.mutate(); }} className="space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
              Informações Básicas
            </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Moeda *</label>
                <select
                  name="currency"
                  value={form.currency || 'BRL'}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.currency ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="BRL">BRL</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição *</label>
                <input
                  type="text"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria *</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.category ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="stocks">Ações</option>
                  <option value="fixed_income">Renda Fixa</option>
                  <option value="funds">Fundos</option>
                  <option value="crypto">Cripto</option>
                  <option value="offshore">Offshore</option>
                  <option value="other">Outros</option>
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.status ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="active">Ativo</option>
                  <option value="sold">Vendido</option>
                </select>
                {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status}</p>}
              </div>
            </div>
          </div>

          {/* Seção: Valores */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
              Valores
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor Investido *</label>
                <input
                  type="text"
                  name="investedValue"
                  value={investedValueDisplay}
                  onChange={e => handleCurrencyChange('investedValue', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.investedValue ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder={formatCurrency(0, form.currency || 'BRL', getCurrencyLocale(form.currency || 'BRL'))}
                  inputMode="decimal"
                />
                {errors.investedValue && <p className="mt-1 text-sm text-red-600">{errors.investedValue}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor Atual (opcional)</label>
                <input
                  type="text"
                  name="currentValue"
                  value={currentValueDisplay}
                  onChange={e => handleCurrencyChange('currentValue', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.currentValue ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder={formatCurrency(0, form.currency || 'BRL', getCurrencyLocale(form.currency || 'BRL'))}
                  inputMode="decimal"
                />
                {errors.currentValue && <p className="mt-1 text-sm text-red-600">{errors.currentValue}</p>}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(`/investments/${id}`)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center disabled:opacity-60"
            >
              <CheckIcon className="h-5 w-5 mr-2" />
              {updateMutation.isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInvestment;
